import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '../../shared/services/auth/auth.service';
import { ToastService } from '../../shared/services/toast/toast.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const token = authService.getToken();

  if (token) {
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    return next(authReq).pipe(
      tap((response) => {
        // Check for new token in response header
        if (response instanceof HttpResponse) {
          const newToken = response.headers.get('X-New-Access-Token');
          if (newToken) {
            authService.updateToken(newToken);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Broker connect / Dhan OAuth 401/403 = broker auth failure. Do NOT treat as app session expired.
        // Let the component handle it (redirect to Dhan OAuth or show friendly message). Never log user out for this.
        const url = typeof req.url === 'string' ? req.url : (req.url as any)?.toString?.() ?? '';
        const isBrokerAuth =
          url.includes('brokers/connect') || url.includes('brokers/dhan/oauth');
        if (isBrokerAuth && (error.status === 401 || error.status === 403)) {
          return throwError(() => error);
        }
        // Handle 401 (Unauthorized) or 403 (Forbidden) for other routes - app session expired
        if (error.status === 401 || error.status === 403) {
          // Clear invalid/expired token
          authService.logout();

          // Show toast notification
          toastService.error('Your session has expired. Please login again.', 'Session Expired');

          // Get current URL before redirecting
          const currentUrl = router.url;

          // Only redirect if we're not already on the login page
          if (!currentUrl.includes('/login')) {
            router.navigate(['/login'], {
              queryParams: {
                returnUrl: currentUrl,
              },
              replaceUrl: true,
            });
          }
        }
        return throwError(() => error);
      }),
    );
  }

  return next(req).pipe(
    tap((response) => {
      // Check for new token even if no token was sent (for initial requests)
      if (response instanceof HttpResponse) {
        const newToken = response.headers.get('X-New-Access-Token');
        if (newToken) {
          authService.updateToken(newToken);
        }
      }
    }),
  );
};
